import { useMemo, useCallback } from "react";
import TestimonialCarousel from "@/components/widgets/testimonial-carousel";
import { useWidgetFont } from "../hooks/use-widget-font";
import type { WidgetTemplateProps } from "./template-router";

const sampleCarouselTestimonials = [
	{
		id: "1",
		content: "There's so much opportunity for interactive video! Curious to learn more about your product while trying it out. Congrats on the launch!",
		authorName: "Carlo Thissen",
		authorHandle: "@carlo_thissen",
		avatarUrl: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face",
	},
	{
		id: "2",
		content: "This is exactly what we needed for our marketing site. The testimonials look amazing and the setup was super easy!",
		authorName: "Jessica Moore",
		authorHandle: "@jessicamoore",
		avatarUrl: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face",
	},
	{
		id: "3",
		content: "Finally a testimonial tool that doesn't look like it's from 2010. Beautiful design and great UX. Highly recommend!",
		authorName: "David Park",
		authorHandle: "@davidpark",
		avatarUrl: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face",
	},
];

export default function TemplateClassicCarousel({ previewData, layoutConfig, templateDetail, brandKit, showBranding = true }: WidgetTemplateProps) {
	const fontStyle = useWidgetFont(layoutConfig, templateDetail);

	const getLayoutValue = useCallback(
		(key: string, fallback: unknown = "") => {
			if (layoutConfig[key] !== undefined) return layoutConfig[key];
			if (templateDetail?.defaults?.layout?.[key] !== undefined) return templateDetail.defaults.layout[key];
			return fallback;
		},
		[layoutConfig, templateDetail],
	);

	const testimonials = useMemo(() => {
		if (!previewData) return sampleCarouselTestimonials;
		if (!("reviews" in previewData) || !previewData.reviews || previewData.reviews.data.length === 0) return sampleCarouselTestimonials;
		return previewData.reviews.data.map((review) => ({
			id: review.id,
			content: review.content_text || "",
			authorName: review.customer_name || "Anonymous",
			authorHandle: review.customer_headline || undefined,
			avatarUrl: review.customer_avatar_url || undefined,
			rating: review.rating || 0,
		}));
	}, [previewData]);

	const autoplay = getLayoutValue("autoplay", false) === true;
	const autoplayInterval = Number(getLayoutValue("autoplay_interval", 5));
	const showNavigation = getLayoutValue("show_navigation", true) !== false;
	const showPagination = getLayoutValue("show_pagination", true) !== false;
	const showRating = getLayoutValue("show_rating", true) !== false;
	const showAvatar = getLayoutValue("show_avatar", true) !== false;
	const bgColor = String(getLayoutValue("background_color", "") || brandKit?.color_accent_hex || "#7c3aed");
	const txtColor = String(getLayoutValue("text_color", "") || "#ffffff");
	const ratingStarColor = String(getLayoutValue("rating_star_color", "") || "") || undefined;

	return (
		<div style={fontStyle}>
			<TestimonialCarousel
				testimonials={testimonials}
				backgroundColor={bgColor}
				textColor={txtColor}
				autoplay={autoplay}
				autoplayInterval={autoplayInterval}
				showNavigation={showNavigation}
				showPagination={showPagination}
				showRating={showRating}
				showAvatar={showAvatar}
				ratingStarColor={ratingStarColor}
			/>

			{/* Powered by branding */}
			{showBranding && (
				<div className="py-4 flex justify-center text-center text-gray-400">
					<a href="https://sayabout.us/" target="_blank" rel="noopener noreferrer" className="underline block hover:opacity-80">
						<img src="/images/badge-sayaboutus.svg" alt="build social proof with Say About Us" className="h-5 w-auto" />
					</a>
				</div>
			)}
		</div>
	);
}
